import React, { useState } from 'react';

import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import DescriptionSharpIcon from '@mui/icons-material/DescriptionSharp';
import { useTheme } from '@mui/material/styles';
import { ThreeDotMenu } from 'common/components/core';
import {
  Card,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from 'common/components/material';
import { CardContent } from 'common/components/material/Card';
import { useDrawer } from 'common/hooks';
import { DrawerIdEnum } from 'features/org-root/enums/drawer-id.enum';
import { Skill } from 'features/skills';
import { useDeleteSkillFeedback } from '../hooks/useDeleteSkillFeedback';
import { useGetPastSkillFeedbacks } from '../hooks/useGetPastSkillFeedback';
import {
  FEEDBACK_MENU_OPTIONS,
  FeedbackList,
} from '../matching-engine.utils';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Accordion, AccordionSummary } from '@mui/material';
import { AccordionDetails } from 'common/components/material/Accordion';

const MIN_INPUT_LENGTH = 4;

type SkillFeedbackCardProps = {
  skill: Skill;
  currentFeedbackCount: number;
  revieweeId: string;
  skillFeedbackCaptionNode: React.ReactNode;
};

export const SkillFeedbackCard: React.FC<SkillFeedbackCardProps> = ({
  skill,
  revieweeId,
  currentFeedbackCount,
  skillFeedbackCaptionNode,
}) => {
  const { palette } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Card sx={{ border: 'none' }}>
      <CardContent
        sx={{
          margin: '0.15rem',
          paddingBottom: '0.1rem !important',
          paddingTop: '0.25rem',
        }}
      >
        <Accordion
          disableGutters
          expanded={isExpanded}
          onChange={() => setIsExpanded(!isExpanded)}
          elevation={0}
          sx={{
            backgroundColor: 'transparent',
            boxShadow: 'none',
            '&:before': { display: 'none' },
            margin: 0,
            padding: 0,
          }}
        >
          <AccordionSummary
            expandIcon={<NavigateNextIcon />}
            sx={{
              paddingX: 0,
              '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                transform: 'rotate(270deg)',
              },
              '& .MuiAccordionSummary-content': {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              },
            }}
          >
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item lg={4} xl={4}>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  fontSize="0.875rem"
                  color={palette.Text.Headline}
                >
                  {skill.name}
                </Typography>
                {skillFeedbackCaptionNode}
              </Grid>

              <Stack alignItems="center" sx={{ cursor: 'pointer', mr: 1 }}>
                <IconButton
                  sx={{
                    color: palette.Icons.Tertiary.IconFillDefault,
                    padding: 0.2,
                  }}
                >
                  <DescriptionSharpIcon fontSize="small" />
                </IconButton>
                <Typography variant="caption" sx={{ color: palette.Text.SubHeadline }}>
                  {currentFeedbackCount}
                </Typography>
              </Stack>
            </Grid>
          </AccordionSummary>

          <AccordionDetails
            sx={{
              width: '100%',
              borderRadius: 2,
            }}
          >
            {isExpanded && <FeedbackCardsList revieweeId={revieweeId} skill={skill} />}
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export const FeedbackCardsList: React.FC<{
  revieweeId: any;
  skill: Skill;
}> = ({ revieweeId, skill }) => {
  const { data, isLoading } = useGetPastSkillFeedbacks(revieweeId, skill.id);

  if (isLoading || !data || !data.pastFeedbacks) return <></>;

  const { pastFeedbacks } = data;
  return <FeedbackList feedbacks={pastFeedbacks} />;
};

export const AddFeedbackText: React.FC<{
  feedbackText: string;
  handleSubmit: () => void;
  handleClear: () => void;
  onChange: Function;
}> = ({ handleSubmit, handleClear, feedbackText, onChange }) => {
  const { palette } = useTheme();
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if ((feedbackText ?? '').length >= MIN_INPUT_LENGTH && event.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <TextField
      placeholder="Add note..."
      fullWidth
      variant="outlined"
      size="small"
      value={feedbackText}
      onKeyDown={handleKeyDown}
      onChange={(e) => onChange(e.target.value)}
      InputProps={{
        endAdornment:
          (feedbackText ?? '').length >= MIN_INPUT_LENGTH ? (
            <InputAdornment position="end">
              <CheckOutlinedIcon onClick={handleSubmit} cursor="pointer" fontSize="small" />
              <ClearOutlinedIcon onClick={handleClear} cursor="pointer" fontSize="small" />
            </InputAdornment>
          ) : (
            <></>
          ),
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          '&:hover fieldset': {
            borderColor: palette.TextFields.Hover,
          },
          '&.Mui-focused fieldset': {
            borderColor: palette.TextFields.Clicked,
          },
        },
        '& .MuiInputBase-input::placeholder': {
          fontWeight: 400,
          opacity: 0.8,
          fontSize: '0.875rem',
          color: palette.EmptyPlaceholderIcons.IconFill,
        },
      }}
    />
  );
};

export const PastFeedbackDropdownMenu: React.FC<{
  profileId: string;
  feedbackId: string;
  skillId: string;
}> = ({ profileId, feedbackId, skillId }) => {
  const { openDrawer, closeDrawer } = useDrawer();

  const { mutateAsync: deleteFeedbacks } = useDeleteSkillFeedback(profileId, skillId);
  const { refetch: refetchPastFeedbacks } = useGetPastSkillFeedbacks(profileId, skillId);

  const openDeleteConfirmation = () => {
    openDrawer(DrawerIdEnum.LEAVE_FORM, {
      headerTitle: 'Delete Feedback?',
      confirmButtonText: 'Delete',
      subTitle: 'Feedback will be permanently deleted',
      confirmButtonHandler: async () => {
        await deleteFeedbacks(feedbackId);
        refetchPastFeedbacks();
        closeDrawer(DrawerIdEnum.LEAVE_FORM);
      },
    });
  };

  const handleSelect = () => {
    openDeleteConfirmation();
  };

  return <ThreeDotMenu items={FEEDBACK_MENU_OPTIONS} onSelect={handleSelect} />;
};
